// components/SuggestionReporter.jsx
// "Suggest an improvement" (💡) header button + modal. Sibling of BugReporter,
// but simpler: it captures a feature idea / piece of feedback — no log/diagnostic
// capture. On submit it POSTs a category + title + description (plus the current
// url/route for triage context) to /api/suggestion. See that proxy + the
// Feedback.Suggestions table for the storage side.

import React, { useState, useEffect } from 'react';

const COLORS = {
  brandDark:  '#003D4C',
  brandGreen: '#6CC24A',
  bg:         '#ffffff',
  text:       '#1f2937',
  muted:      '#6b7280',
  border:     '#e5e7eb',
  cardBg:     '#f8fafc',
};

const CATEGORIES = [
  { v: 'new_feature', t: 'New Feature' },
  { v: 'improvement', t: 'Improvement' },
  { v: 'ui',          t: 'UI polish' },
  { v: 'other',       t: 'Other' },
];

// One-shot cold-start retry (mirrors BugReporter — kept inline so this component
// carries no app-specific imports).
async function postWithRetry(url, payload) {
  const opts = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  };
  const start = Date.now();
  let r;
  try {
    r = await fetch(url, opts);
  } catch (netErr) {
    if (Date.now() - start < 5000) { await new Promise(res => setTimeout(res, 2000)); return fetch(url, opts); }
    throw netErr;
  }
  if (!r.ok && r.status >= 500 && Date.now() - start < 5000) {
    await new Promise(res => setTimeout(res, 2000));
    return fetch(url, opts);
  }
  return r;
}

export default function SuggestionReporter({
  authState = {}, contextApp = 'rprs', apiUrl = '/api/suggestion',
  // "My suggestions" tab + 💡 pulse. pulse = the user has a status change they
  // haven't seen; onOpen fires when the modal opens (clear the pulse / mark
  // suggestion_updated notifications read). listUrl = the read proxy.
  pulse = false, onOpen, listUrl = '/api/suggestions',
  // openSignal: bump this counter (from AppShell) to open the modal programmatically
  // — e.g. when the feature-awareness bubble is clicked. 0 on mount = no-op.
  openSignal = 0,
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('suggest');    // 'suggest' | 'mine'
  const [category, setCategory] = useState('new_feature');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // { suggestionId }

  // "My suggestions" tab state (lazy-loaded on first view per open).
  const [mine, setMine] = useState(null); // null = not loaded yet
  const [mineLoading, setMineLoading] = useState(false);
  const [mineError, setMineError] = useState(null);

  const reset = () => {
    setCategory('new_feature'); setTitle(''); setDescription('');
    setBusy(false); setError(null); setDone(null);
  };
  const close = () => { setOpen(false); reset(); setTab('suggest'); setMine(null); };

  // Open: if there's an unseen status change, land on "My suggestions"; either
  // way tell the parent (clears the pulse / marks the notifications read).
  const openModal = () => {
    setOpen(true);
    setTab(pulse ? 'mine' : 'suggest');
    try { onOpen?.(); } catch (_) { /* ignore */ }
  };

  // Open programmatically when AppShell bumps openSignal (awareness bubble click).
  // openSignal starts at 0 → the mount run is a no-op; only a later bump opens.
  useEffect(() => { if (openSignal) openModal(); }, [openSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMine = async () => {
    setMineLoading(true); setMineError(null);
    try {
      const r = await postWithRetry(listUrl, { scope: 'mine' });
      if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(`(${r.status}) ${t}`); }
      const data = await r.json();
      setMine(Array.isArray(data.suggestions) ? data.suggestions : []);
    } catch (e) {
      setMineError(e?.message || String(e));
      setMine([]);
    } finally {
      setMineLoading(false);
    }
  };

  // Lazy-load my suggestions the first time the tab is shown while open.
  useEffect(() => {
    if (open && tab === 'mine' && mine === null && !mineLoading) loadMine();
  }, [open, tab, mine, mineLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = title.trim().length > 0 || description.trim().length > 0;

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        contextApp,
        category,
        title: title.trim().slice(0, 200),
        description: description.trim(),
        context: { url: window.location.href, route: window.location.pathname },
      };
      const r = await postWithRetry(apiUrl, payload);
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        throw new Error(`Submit failed (${r.status}): ${txt}`);
      }
      const data = await r.json();
      setDone({ suggestionId: data.suggestionId });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className={`app-suggestion-btn${pulse ? ' app-suggestion-btn--pulse' : ''}`}
        title={pulse ? 'A suggestion you made was updated' : 'Suggest an improvement'}
        aria-label="Suggest an improvement"
        onClick={openModal}
      >
        &#128161;{/* 💡 */}
      </button>

      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) close(); }}
        >
          <div style={{
            background: COLORS.bg, color: COLORS.text, borderRadius: 14,
            width: 'min(580px, 94vw)', maxHeight: '90vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,.35)',
          }}>
            {/* Header */}
            <div style={{
              background: COLORS.brandDark, color: 'white', padding: '18px 24px',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: 26, lineHeight: 1 }}>&#128161;</span>
              <div>
                <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.1 }}>Suggest an improvement</div>
                <div style={{ fontSize: 13, color: '#cbe5d8', marginTop: 2 }}>
                  Share an idea or piece of feedback — we'll route it to the team.
                </div>
              </div>
            </div>

            {/* Tab strip */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, background: '#fff' }}>
              <TabBtn active={tab === 'suggest'} onClick={() => setTab('suggest')}>Suggest</TabBtn>
              <TabBtn active={tab === 'mine'} onClick={() => setTab('mine')}>My suggestions</TabBtn>
            </div>

            {tab === 'mine' ? (
              <MinePanel
                items={mine} loading={mineLoading} error={mineError}
                onSuggest={() => setTab('suggest')}
              />
            ) : done ? (
              // Success state
              <div style={{ padding: '28px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 10 }}>&#9989;{/* ✅ */}</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: COLORS.brandDark }}>Thanks — suggestion sent.</div>
                <p style={{ fontSize: 13.5, color: COLORS.muted, margin: '8px 0 0', lineHeight: 1.5 }}>
                  Reference <strong>#{done.suggestionId}</strong>. The team will review it and you'll get a
                  notification when it's planned, shipped, or declined.
                </p>
                <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button onClick={close} style={primaryBtn}>Close</button>
                  <button onClick={reset} style={secondaryBtn}>Suggest another</button>
                </div>
              </div>
            ) : (
              <>
                {/* Body */}
                <div style={{ padding: '18px 24px', overflowY: 'auto', fontSize: 14 }}>
                  <Field label="Category">
                    <select value={category} onChange={(e) => setCategory(e.target.value)} style={selectStyle}>
                      {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.t}</option>)}
                    </select>
                  </Field>

                  <Field label="Title" required>
                    <input
                      value={title} onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. Let me filter the grid by client industry"
                      style={inputStyle} autoFocus
                    />
                  </Field>

                  <Field label="Describe your idea">
                    <textarea
                      value={description} onChange={(e) => setDescription(e.target.value)}
                      placeholder="What would you like to see, and why would it help?"
                      rows={4} style={textareaStyle}
                    />
                  </Field>

                  {error && (
                    <div style={{ marginTop: 4, background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6, fontSize: 13 }}>
                      {error}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div style={{
                  padding: '14px 24px 18px', borderTop: `1px solid ${COLORS.border}`,
                  display: 'flex', gap: 10, justifyContent: 'flex-end',
                }}>
                  <button onClick={close} disabled={busy} style={secondaryBtn}>Cancel</button>
                  <button onClick={submit} disabled={!canSubmit || busy}
                    style={{ ...primaryBtn, opacity: (!canSubmit || busy) ? 0.55 : 1, cursor: (!canSubmit || busy) ? 'not-allowed' : 'pointer' }}>
                    {busy ? 'Sending…' : 'Send suggestion'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        flex: 1, padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 13, fontWeight: 700, color: active ? COLORS.brandDark : COLORS.muted,
        borderBottom: active ? `2px solid ${COLORS.brandGreen}` : '2px solid transparent',
      }}>
      {children}
    </button>
  );
}

const STATUS_LABELS = {
  under_review: { t: 'Under review', c: '#92400E', b: '#FEF3C7' },
  planned:      { t: 'Planned',      c: '#1D4ED8', b: '#DBEAFE' },
  shipped:      { t: 'Shipped',      c: '#065F46', b: '#D1FAE5' },
  declined:     { t: 'Declined',     c: '#6B7280', b: '#F3F4F6' },
};

const CATEGORY_LABELS = CATEGORIES.reduce((m, c) => { m[c.v] = c.t; return m; }, {});

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
}

// Read-only list of the signed-in user's own suggestions + their current status/note.
function MinePanel({ items, loading, error, onSuggest }) {
  return (
    <div style={{ padding: '18px 24px', overflowY: 'auto', fontSize: 14, minHeight: 160 }}>
      {loading ? (
        <div style={{ textAlign: 'center', color: COLORS.muted, padding: 24, fontSize: 13 }}>Loading your suggestions…</div>
      ) : error ? (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6, fontSize: 13 }}>
          Couldn't load your suggestions: {error}
        </div>
      ) : !items || items.length === 0 ? (
        <div style={{ textAlign: 'center', color: COLORS.muted, padding: '28px 12px' }}>
          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>&#128161;</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>You haven't made any suggestions yet.</div>
          <button onClick={onSuggest} style={{ ...secondaryBtn, marginTop: 14 }}>Suggest an improvement</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(s => {
            const st = STATUS_LABELS[s.status] || STATUS_LABELS.under_review;
            return (
              <div key={s.suggestionId} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '10px 12px', background: COLORS.cardBg }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontWeight: 600, color: COLORS.text, fontSize: 13.5, lineHeight: 1.3 }}>
                    {s.title || '(no title)'}
                  </div>
                  <span style={{ flexShrink: 0, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: st.c, background: st.b, whiteSpace: 'nowrap' }}>
                    {st.t}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 3 }}>
                  #{s.suggestionId} · {CATEGORY_LABELS[s.category] || 'Other'} · {fmtDate(s.createdAt)}
                </div>
                {s.resolutionNote && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700, color: COLORS.brandDark }}>Update from the team: </span>
                    {s.resolutionNote}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px',
  fontSize: 13.5, fontFamily: 'inherit', lineHeight: 1.45, color: '#1f2937',
};
const textareaStyle = { ...inputStyle, resize: 'vertical' };
const selectStyle = { ...inputStyle, cursor: 'pointer' };
const primaryBtn = {
  padding: '10px 22px', background: COLORS.brandGreen, color: 'white',
  border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const secondaryBtn = {
  padding: '10px 18px', background: 'white', color: COLORS.muted,
  border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
